import { useState, useEffect } from 'react';
import { api } from '../api';
import StudentIdCard, { QrAttendanceScanner } from './StudentIdCard';

const EMPTY = {
  admissionNo: '', firstName: '', lastName: '', gender: 'Male', dob: '',
  fatherName: '', motherName: '', phone: '', email: '', address: '',
  admissionDate: '', bloodGroup: '', rollNo: '', status: 'Active',
  classSection: { id: '' }, photo: ''
};

function fullName(s) { return s ? `${s.firstName || ''} ${s.lastName || ''}`.trim() : ''; }
function className(c) { return c ? `${c.name} - ${c.section}` : '—'; }
function initials(s) {
  const n = fullName(s);
  return n.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';
}

export default function Students() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('list');
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [err, setErr] = useState('');
  const [idCardStudent, setIdCardStudent] = useState(null);
  const [showScanner, setShowScanner]     = useState(false);
  const [schoolName, setSchoolName]       = useState('');

  useEffect(() => {
    Promise.all([api.getStudents(), api.getClasses()])
      .then(([s, c]) => { setStudents(s); setClasses(c); })
      .catch(() => setErr('Failed to load data'))
      .finally(() => setLoading(false));
    fetch('/spring/school/auth/me', { credentials: 'include' })
      .then(r => r.json()).then(d => setSchoolName(d.businesstitle || '')).catch(() => {});
  }, []);

  const filtered = students.filter(s => {
    const name = fullName(s);
    const matchSearch = !search || name.toLowerCase().includes(search.toLowerCase()) ||
      (s.admissionNo || '').toLowerCase().includes(search.toLowerCase());
    const matchClass = !filterClass || s.classSection?.id === parseInt(filterClass);
    return matchSearch && matchClass;
  });

  const openAdd = () => { setForm(EMPTY); setEditId(null); setTab('add'); setErr(''); };
  const openEdit = (s) => {
    setForm({
      ...s,
      dob: s.dob ? s.dob.split('T')[0] : '',
      admissionDate: s.admissionDate ? s.admissionDate.split('T')[0] : '',
      classSection: { id: s.classSection?.id ?? '' },
      photo: s.photo || ''
    });
    setEditId(s.id);
    setTab('add');
    setErr('');
  };

  const handleSave = async () => {
    if (!form.firstName || !form.admissionNo) { setErr('First name and Admission No are required'); return; }
    const payload = { ...form, classSection: form.classSection?.id ? { id: parseInt(form.classSection.id) } : null };
    try {
      if (editId) await api.updateStudent(editId, payload);
      else await api.createStudent(payload);
      window.location.reload();
    } catch (e) { setErr(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this student?')) return;
    await api.deleteStudent(id);
    window.location.reload();
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (loading) return <div className="loading-msg">Loading students…</div>;

  const feesBadge = (status) => {
    if (!status) return <span className="badge badge-gray">—</span>;
    const s = status.toLowerCase();
    if (s === 'active') return <span className="badge badge-green">Paid</span>;
    return <span className="badge badge-gray">{status}</span>;
  };

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'list' ? ' active' : ''}`} onClick={() => setTab('list')}>Student List</button>
        <button className={`tab${tab === 'add' ? ' active' : ''}`} onClick={openAdd}>Add Student</button>
      </div>

      {/* ── LIST TAB ── */}
      {tab === 'list' && (
        <div>
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">All Students</div>
                <div className="card-subtitle">{filtered.length} students enrolled</div>
              </div>
              <div className="card-actions">
                <div className="search-bar">
                  <i className="fas fa-search"></i>
                  <input type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <select className="form-control" style={{ width: 130 }} value={filterClass} onChange={e => setFilterClass(e.target.value)}>
                  <option value="">All Classes</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{className(c)}</option>)}
                </select>
                <button className="btn btn-primary btn-sm" onClick={openAdd}>
                  ➕ Add Student
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => setShowScanner(true)} title="QR Attendance Scanner">
                  📷 Scan QR
                </button>
              </div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th><th>Student Name</th><th>Adm No</th><th>Class</th>
                    <th>Father</th><th>Phone</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: 30 }}>No students found</td></tr>
                  )}
                  {filtered.map((s, i) => (
                    <tr key={s.id}>
                      <td>{i + 1}</td>
                      <td>
                        <div className="chip">
                          {s.photo
                            ? <img src={s.photo} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                            : <div className="chip-avatar">{initials(s)}</div>
                          }
                          <span className="chip-text">{fullName(s)}</span>
                        </div>
                      </td>
                      <td><strong>{s.admissionNo}</strong></td>
                      <td>{s.classSection ? className(s.classSection) : '—'}</td>
                      <td>{s.fatherName}</td>
                      <td>{s.phone}</td>
                      <td>
                        <span className={`badge ${s.status?.toLowerCase() === 'active' ? 'badge-green' : 'badge-red'}`}>
                          {s.status || '—'}
                        </span>
                      </td>
                      <td>
                        <div className="card-actions">
                          <button className="btn btn-outline btn-sm btn-icon" onClick={() => openEdit(s)} title="Edit">✏️</button>
                          <button className="btn btn-outline btn-sm btn-icon" onClick={() => setIdCardStudent(s)} title="Print ID Card">🪪</button>
                          <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(s.id)} title="Delete">🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT TAB ── */}
      {tab === 'add' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">{editId ? 'Edit Student' : 'Add New Student'}</div>
          </div>
          <div className="card-body">
            {err && <div className="error-msg">{err}</div>}
            <div className="form-grid cols-3" style={{ marginBottom: 14 }}>
              <div className="form-group">
                <label className="form-label">Admission No *</label>
                <input className="form-control" value={form.admissionNo} onChange={e => set('admissionNo', e.target.value)} placeholder="e.g. 2024001" />
              </div>
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input className="form-control" value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder="Enter first name" />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input className="form-control" value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder="Enter last name" />
              </div>
              <div className="form-group">
                <label className="form-label">Date of Birth</label>
                <input className="form-control" type="date" value={form.dob} onChange={e => set('dob', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Gender</label>
                <select className="form-control" value={form.gender} onChange={e => set('gender', e.target.value)}>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Blood Group</label>
                <input className="form-control" value={form.bloodGroup} onChange={e => set('bloodGroup', e.target.value)} placeholder="A+, B-, O+…" />
              </div>
              <div className="form-group">
                <label className="form-label">Class</label>
                <select className="form-control" value={form.classSection?.id ?? ''}
                  onChange={e => setForm(f => ({ ...f, classSection: { id: e.target.value } }))}>
                  <option value="">-- Select Class --</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{className(c)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Roll Number</label>
                <input className="form-control" type="number" value={form.rollNo} onChange={e => set('rollNo', e.target.value)} placeholder="Auto-assigned" />
              </div>
              <div className="form-group">
                <label className="form-label">Admission Date</label>
                <input className="form-control" type="date" value={form.admissionDate} onChange={e => set('admissionDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
            <hr style={{ border: 'none', borderTop: '1px solid #f0f4f8', margin: '16px 0' }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 10 }}>Student Photo</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              {form.photo ? (
                <img src={form.photo} alt="Student" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', border: '2px solid #e2e8f0' }} />
              ) : (
                <div style={{ width: 80, height: 80, borderRadius: 8, background: '#e8f0fb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, border: '2px dashed #c3d4f0' }}>
                  👤
                </div>
              )}
              <div>
                <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer', marginBottom: 4 }}>
                  📷 {form.photo ? 'Change Photo' : 'Upload Photo'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = ev => set('photo', ev.target.result);
                    reader.readAsDataURL(file);
                  }} />
                </label>
                {form.photo && (
                  <div>
                    <button className="btn btn-outline btn-sm" style={{ color: '#e53e3e', borderColor: '#e53e3e' }} onClick={() => set('photo', '')}>
                      🗑️ Remove
                    </button>
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#718096', marginTop: 4 }}>JPG, PNG — shown on ID card</div>
              </div>
            </div>
            <hr style={{ border: 'none', borderTop: '1px solid #f0f4f8', margin: '16px 0' }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 10 }}>Parent / Guardian Details</div>
            <div className="form-grid cols-3">
              <div className="form-group">
                <label className="form-label">Father's Name</label>
                <input className="form-control" value={form.fatherName} onChange={e => set('fatherName', e.target.value)} placeholder="Father's name" />
              </div>
              <div className="form-group">
                <label className="form-label">Mother's Name</label>
                <input className="form-control" value={form.motherName} onChange={e => set('motherName', e.target.value)} placeholder="Mother's name" />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number *</label>
                <input className="form-control" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="10-digit number" />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
              </div>
              <div className="form-group full">
                <label className="form-label">Address</label>
                <textarea className="form-control" value={form.address} onChange={e => set('address', e.target.value)} placeholder="Full residential address" />
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-outline" onClick={() => { setTab('list'); setEditId(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>
                💾 {editId ? 'Update Student' : 'Save Student'}
              </button>
            </div>
          </div>
        </div>
      )}

      {idCardStudent && (
        <StudentIdCard
          student={idCardStudent}
          schoolName={schoolName}
          onClose={() => setIdCardStudent(null)}
        />
      )}

      {showScanner && (
        <QrAttendanceScanner onClose={() => setShowScanner(false)} />
      )}
    </div>
  );
}
